import axiosInstance from './api/axiosInstance';

export interface CartItem {
  itemType: 'product' | 'service';
  itemId: string;
  quantity: number;
  variant?: {
    name: string;
    option: string;
  };
  packageId?: string;
  packageName?: string;
  name: string;
  price: number;
  currency: string;
  image?: string;
  vendorId: string;
  vendorName: string;
  metadata?: Record<string, any>;
}

export interface Cart {
  _id?: string;
  user: string;
  items: CartItem[];
  totals: {
    subtotal: number;
    discount: number;
    charges?: Array<{ name: string; type: string; value: number; amount: number }>;
    total: number;
  };
  couponCode?: string;
  itemCount?: number;
  uniqueItemCount?: number;
  lastUpdated?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AddToCartRequest {
  itemType: 'product' | 'service';
  itemId: string;
  quantity?: number;
  variant?: {
    name: string;
    option: string;
  };
  packageId?: string;
}

class CartService {
  /**
   * Get user's cart
   */
  async getCart(): Promise<{ success: boolean; data: Cart }> {
    const response = await axiosInstance.get('/cart');
    return response.data;
  }

  /**
   * Add item to cart
   */
  async addToCart(data: AddToCartRequest): Promise<{ success: boolean; data: Cart; message: string }> {
    const response = await axiosInstance.post('/cart/add', {
      itemType: data.itemType,
      itemId: data.itemId,
      quantity: data.quantity || 1,
      ...(data.variant && { variant: data.variant }),
      ...(data.packageId && { packageId: data.packageId }),
    });
    return response.data;
  }

  /**
   * Update item quantity in cart
   */
  async updateItemQuantity(itemIndex: number, quantity: number): Promise<{ success: boolean; data: Cart; message: string }> {
    const response = await axiosInstance.put(`/cart/item/${itemIndex}`, { quantity });
    return response.data;
  }

  /**
   * Remove item from cart
   */
  async removeItem(itemIndex: number): Promise<{ success: boolean; data: Cart; message: string }> {
    const response = await axiosInstance.delete(`/cart/item/${itemIndex}`);
    return response.data;
  }

  /**
   * Clear entire cart
   */
  async clearCart(): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete('/cart/clear');
    return response.data;
  }

  /**
   * Apply coupon to cart
   */
  async applyCoupon(code: string): Promise<{
    success: boolean;
    data: {
      cart: Cart;
      coupon: {
        code: string;
        name: string;
        discount: number;
        discountType: string;
        discountValue: number;
      };
    };
    message: string;
  }> {
    const response = await axiosInstance.post('/cart/coupon/apply', { code });
    return response.data;
  }

  /**
   * Remove coupon from cart
   */
  async removeCoupon(): Promise<{ success: boolean; data: Cart; message: string }> {
    const response = await axiosInstance.delete('/cart/coupon/remove');
    return response.data;
  }
}

export const cartService = new CartService();








